import { IFormHandler } from "client/Forms";
import { InputValidationResult, InputValidationResultKind } from "client/Validation";
import { ReactElement, useCallback } from "react";
import {
	CommunityPostImageFormData,
	CommunityPostImageFormModelKind,
	PostComposerFormData,
	convertToCommunityPostSaveModel
} from "./common";
import {
	AlertManager,
	useAlerts,
	useUnmountedCancellation
} from "client/Hooks";
import { BulletList } from "client/Components";
import { CombinedInputValidationResult } from "client/Validation/state";
import {
	ensureNotNil,
	isNil,
	isNotNil
} from "client/Misc/utils";
import {
	CommunityPostSaveModel,
	createCommunityPost,
	editCommunityPost
} from "client/Api/Communities/communityPosts";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { unique } from "client_legacy/ndaw/arrayHelpers";
import { localize } from "client_legacy/ndaw/culture";
import { InvalidInputValidationResult } from "client/Validation/input";
import { tryParseServerErrors } from "client/Validation/serverErrors";
import { ModelPath } from "client/Validation/path";
import { CommunityPostDetailsViewModel } from "client/Api/Communities/communityPostViewer";
import { IValueInputAttachable } from "client/Forms/attachable";
import { ObjectWithClientId } from "client/Misc/clientId";

export function useSavePost(
	isBusy: boolean,
	setIsBusy: (value: boolean) => void,
	formHandler: IFormHandler<PostComposerFormData>,
	isMobile: boolean
): (() => Promise<TryResult<CommunityPostDetailsViewModel>>) {
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const alertManager: AlertManager = useAlerts();

	return useCallback(
		async (): Promise<TryResult<CommunityPostDetailsViewModel>> => {
			const formData: PostComposerFormData = formHandler.value;

			if (
				isBusy
				|| formData.images
					.some(x => x.kind === CommunityPostImageFormModelKind.Loading)
			) {
				return { wasSuccessful: false };
			}

			setIsBusy(true);

			try {
				const validationResult: InputValidationResult = await formHandler.validation
					.validate(globalCancellation);

				if (validationResult.kind !== InputValidationResultKind.Valid) {
					if (isMobile) {
						const messageValidationState: CombinedInputValidationResult = formHandler.for("message")
							.validation
							.getValidationState();

						if (messageValidationState.kind === InputValidationResultKind.Invalid) {
							alertManager.showError(messageValidationState.errorMessage);
						}
					}

					setIsBusy(false);
					return { wasSuccessful: false };
				}

				const formData: PostComposerFormData = formHandler.value;
				const saveModel: CommunityPostSaveModel = convertToCommunityPostSaveModel(formData);
				let result: CommunityPostDetailsViewModel;

				if (isNil(formData.id)) {
					result = await createCommunityPost(
						ensureNotNil(formData.communityId),
						saveModel,
						globalCancellation
					);
				} else {
					result = await editCommunityPost(
						formData.id,
						saveModel,
						globalCancellation
					);
				}

				setIsBusy(false);

				return {
					wasSuccessful: true,
					result
				};
			} catch (e) {
				if (isCancelledError(e)) {
					setIsBusy(false);
					return { wasSuccessful: false };
				}

				setIsBusy(false);
				const currentPostId: number | null = formHandler.value.id;

				if (formHandler.validation.tryProcessServerError(e)) {
					const {
						images: currentImages
					} = formHandler.value;

					const result: TryResult<CommunityPostServerError> = tryParsePostServerError(
						e,
						currentImages.length
					);

					if (!result.wasSuccessful) {
						alertManager.showError(localize("_GeneralErrorMessage"));
						return { wasSuccessful: false };
					}

					const { imageErrors, messageError } = result.result;

					if (imageErrors.length > 0) {
						const indexesToRemove: ReadonlySet<number> = new Set(imageErrors.map(x => x.index));

						const remainingImages: readonly ObjectWithClientId<CommunityPostImageFormData>[] = currentImages.filter(
							(_, index) => !indexesToRemove.has(index)
						);

						const imagesAttachable: IValueInputAttachable<readonly ObjectWithClientId<CommunityPostImageFormData>[]> = formHandler.for("images");

						imagesAttachable.reducer
							.setValue(remainingImages);

						imagesAttachable.validation
							.triggerValueChanged();

						const uniqueValidationMessages: readonly string[] = unique(
							imageErrors.map(x => x.message)
						);

						alertManager.showError(
							<ImageValidationAlertContent
								messages={uniqueValidationMessages}
							/>
						);
					}

					if (isNotNil(messageError) && isMobile) {
						alertManager.showError(messageError);
					}
				} else {
					console.error(e);
					alertManager.showError(
						localize(
							isNil(currentPostId)
								? "_ErrorOccurredWhileTryingToCreateThePostPleaseTryAgain"
								: "_GeneralErrorMessage"
						)
					);
				}

				return { wasSuccessful: false };
			}
		},
		[
			isBusy,
			setIsBusy,
			formHandler,
			globalCancellation,
			isMobile,
			alertManager
		]
	);
}

type CommunityPostServerError = {
	imageErrors: readonly CommunityPostImageServerError[];
	messageError?: string;
};

type CommunityPostImageServerError = {
	index: number;
	message: string;
};

function tryParsePostServerError(
	error: unknown,
	amountOfImages: number
): TryResult<CommunityPostServerError> {
	const result: TryResult<ReadonlyMap<ModelPath, InvalidInputValidationResult>> = tryParseServerErrors(
		error
	);

	if (!result.wasSuccessful) {
		return result;
	}

	const imageErrors: CommunityPostImageServerError[] = [];
	for (const [path, validationState] of result.result.entries()) {
		const indexResult: TryResult<number> = tryGetImageIndex(path, amountOfImages);
		if (!indexResult.wasSuccessful) {
			continue;
		}

		imageErrors.push({
			index: indexResult.result,
			message: validationState.errorMessage
		});
	}

	return {
		wasSuccessful: true,
		result: {
			imageErrors,
			messageError: result.result
				.get(ModelPath.root.for("message"))
				?.errorMessage
		}
	};
}

function tryGetImageIndex(path: ModelPath, imageCount: number): TryResult<number> {
	const imageRootPath: ModelPath = ModelPath.root
		.for("images");

	for (let index = 0; index < imageCount; index++) {
		const imagePath: ModelPath = imageRootPath.forIndex(index);

		if (imagePath.equals(path) || imagePath.isAncestorOf(path)) {
			return { wasSuccessful: true, result: index };
		}
	}

	return { wasSuccessful: false };
}

type ImageValidationAlertContentProps = {
	messages: readonly string[];
};

const ImageValidationAlertContent = (props: ImageValidationAlertContentProps): ReactElement => {
	const {
		messages
	} = props;

	return (
		<div>
			{localize("_SomeImagesWereRemoved")}
			<BulletList trackingId="server-image-validation-errors">
				{messages.map(
					x => (
						<BulletList.Item key={x}>
							{x}
						</BulletList.Item>
					)
				)}
			</BulletList>
		</div>
	);
};
